import { CoinIcon } from '../components/ui/CoinIcon';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import {
  ArrowLeft, Download, FileText, BookOpen, Calendar,
  ClipboardList, LayoutGrid, Clock, CheckCircle, Clock3,
  XCircle, AlertCircle, User, School, BookMarked
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPE_STYLE = {
  'RPP':        { bg: 'bg-blue-100',   text: 'text-blue-700',   icon: FileText,     gradient: 'from-blue-600 to-blue-800' },
  'Modul Ajar': { bg: 'bg-purple-100', text: 'text-purple-700', icon: BookOpen,     gradient: 'from-purple-600 to-purple-800' },
  'KKTP':       { bg: 'bg-green-100',  text: 'text-green-700',  icon: ClipboardList,gradient: 'from-green-600 to-green-800' },
  'Promes':     { bg: 'bg-orange-100', text: 'text-orange-700', icon: Calendar,     gradient: 'from-orange-500 to-orange-700' },
  'Prota':      { bg: 'bg-pink-100',   text: 'text-pink-700',   icon: LayoutGrid,   gradient: 'from-pink-500 to-pink-700' },
  'Jadwal':     { bg: 'bg-cyan-100',   text: 'text-cyan-700',   icon: Clock,        gradient: 'from-cyan-500 to-cyan-700' },
};

const STATUS_STYLE = {
  draft:          { label: 'Draft',            bg: 'bg-slate-100', text: 'text-slate-600', icon: Clock3 },
  pending_review: { label: 'Menunggu Review',  bg: 'bg-yellow-100',text: 'text-yellow-700',icon: AlertCircle },
  approved:       { label: 'Disetujui',        bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  rejected:       { label: 'Ditolak',          bg: 'bg-red-100',   text: 'text-red-700',   icon: XCircle },
};

// ── Parse Error Banner ─────────────────────────────────────────────────────

const ParseErrorBanner = ({ docId, onNavigate }) => {
  const [refunding, setRefunding] = useState(false);
  const [refunded, setRefunded] = useState(false);
  const [refundError, setRefundError] = useState('');

  const handleRefund = async () => {
    setRefunding(true);
    setRefundError('');
    try {
      await axios.post(`${API}/documents/${docId}/refund`, {}, { withCredentials: true });
      setRefunded(true);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setRefundError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setRefunding(false);
    }
  };

  return (
    <div className="text-center py-12">
      <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={26} className="text-red-400" />
      </div>
      <h3 className="font-bold text-slate-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Dokumen Gagal Dibuat
      </h3>
      <p className="text-slate-500 text-sm mb-2">
        AI tidak berhasil menghasilkan dokumen yang valid saat generate berlangsung.
      </p>
      {refunded ? (
        <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-5 inline-block">
          <p className="text-green-700 text-sm font-semibold">✓ Coin berhasil dikembalikan ke akun Anda.</p>
        </div>
      ) : (
        <>
          <p className="text-slate-400 text-xs mb-5">
            Koin yang dipakai akan dikembalikan jika belum. Lalu buat ulang dokumen ini.
          </p>
          {refundError && (
            <p className="text-red-500 text-xs mb-3">{refundError}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-2">
            <button
              onClick={handleRefund}
              disabled={refunding}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #059669, #10B981)' }}>
              {refunding ? 'Memproses...' : 'Kembalikan Coin'}
            </button>
            <button
              onClick={onNavigate}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, #1E3A8A, #3B82F6)' }}>
              🔄 Buat Ulang Dokumen
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ── Renderer per jenis dokumen ──────────────────────────────────────────────

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h3 className="font-bold text-blue-900 text-sm uppercase tracking-wide mb-3 flex items-center gap-2"
      style={{ fontFamily: "'Outfit', sans-serif" }}>
      <span className="w-1 h-4 rounded-full inline-block" style={{ background: 'linear-gradient(#1E3A8A,#3B82F6)' }} />
      {title}
    </h3>
    {children}
  </div>
);

const InfoGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 gap-2">
    {items.filter(([,v]) => v).map(([label, value]) => (
      <div key={label} className="bg-slate-50 rounded-xl px-4 py-2.5">
        <div className="text-xs text-slate-400 font-medium mb-0.5">{label}</div>
        <div className="text-sm text-slate-800 font-semibold">{value}</div>
      </div>
    ))}
  </div>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5">
    {(items || []).map((item, i) => (
      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
        <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0 mt-0.5"
          style={{ background: 'linear-gradient(135deg,#1E3A8A,#3B82F6)' }}>{i + 1}</span>
        {item}
      </li>
    ))}
  </ul>
);

const KegiatanTable = ({ kegiatan }) => {
  if (!kegiatan) return null;
  const rows = [
    { key: 'pendahuluan', label: 'Pendahuluan', color: 'bg-blue-50 border-blue-200' },
    { key: 'inti',        label: 'Inti',        color: 'bg-slate-50 border-slate-200' },
    { key: 'penutup',     label: 'Penutup',     color: 'bg-green-50 border-green-200' },
  ];
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      {rows.map(({ key, label, color }) => {
        const data = kegiatan[key] || {};
        return (
          <div key={key} className={`border-b last:border-0 ${color} border`}>
            <div className="flex items-center gap-3 px-4 py-2 border-b border-inherit">
              <span className="font-bold text-sm text-slate-800">{label}</span>
              {data.durasi && <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border">{data.durasi}</span>}
            </div>
            <ul className="px-4 py-3 space-y-1.5">
              {(data.langkah || data.kegiatan || []).map((l, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                  <span className="text-slate-400 flex-shrink-0">{i + 1}.</span>{l}
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const RppView = ({ data }) => (
  <div>
    <Section title="Identitas Dokumen">
      <InfoGrid items={[
        ['Mata Pelajaran', data.header?.mata_pelajaran],
        ['Kelas / Semester', data.header?.kelas_semester],
        ['Fase', data.header?.fase],
        ['Materi Pokok', data.header?.materi_pokok],
        ['Alokasi Waktu', data.header?.alokasi_waktu],
        ['Tahun Ajaran', data.header?.tahun_ajaran],
        ['Model Pembelajaran', data.model_pembelajaran],
        ['Profil P. Pancasila', data.profil_pelajar_pancasila?.join(', ')],
      ]} />
    </Section>
    <Section title="Tujuan Pembelajaran">
      <BulletList items={data.tujuan_pembelajaran} />
    </Section>
    <Section title="Kegiatan Pembelajaran">
      <KegiatanTable kegiatan={data.kegiatan_pembelajaran} />
    </Section>
    <Section title="Penilaian">
      {data.penilaian && (
        <div className="space-y-3">
          {['sikap','pengetahuan','keterampilan'].map(k => {
            const p = data.penilaian[k];
            if (!p) return null;
            return (
              <div key={k} className="bg-slate-50 rounded-xl p-4">
                <div className="font-semibold text-sm text-slate-800 capitalize mb-1">{k}</div>
                <div className="text-xs text-slate-500">Teknik: {p.teknik} &nbsp;|&nbsp; Instrumen: {p.instrumen}</div>
                {p.contoh_soal?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs text-slate-400 mb-1">Contoh Soal:</div>
                    {p.contoh_soal.map((s,i) => <div key={i} className="text-xs text-slate-600">• {s}</div>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Section>
    {(data.remedial || data.pengayaan) && (
      <Section title="Remedial & Pengayaan">
        {data.remedial && <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 mb-2"><span className="font-semibold text-orange-700 text-sm">Remedial: </span><span className="text-sm text-slate-700">{data.remedial}</span></div>}
        {data.pengayaan && <div className="bg-blue-50 border border-blue-100 rounded-xl p-4"><span className="font-semibold text-blue-700 text-sm">Pengayaan: </span><span className="text-sm text-slate-700">{data.pengayaan}</span></div>}
      </Section>
    )}
  </div>
);

const ModulAjarView = ({ data }) => {
  // Support both new flat format and old nested format
  const isNewFormat = !!data.identitas;
  const identitas   = data.identitas || {};
  const modelPP     = data.model_pembelajaran || {};
  const langkah     = data.langkah_pembelajaran || {};
  const asesmen     = data.asesmen || {};
  const formatif    = asesmen.formatif || {};

  if (isNewFormat) {
    return (
      <div>
        <Section title="Identitas Modul Ajar">
          <InfoGrid items={[
            ['Mata Pelajaran',    identitas.mata_pelajaran],
            ['Fase',              `Fase ${identitas.fase}`],
            ['Kelas',             `Kelas ${identitas.kelas} ${identitas.jenjang}`],
            ['Semester',          identitas.semester],
            ['Tahun Pelajaran',   identitas.tahun_pelajaran],
            ['Alokasi Waktu',     identitas.alokasi_waktu],
            ['Pertemuan ke-',     identitas.pertemuan_ke],
          ]} />
        </Section>
        <Section title="Informasi Pembelajaran">
          <InfoGrid items={[
            ['Kompetensi Awal',         data.kompetensi_awal],
            ['Profil Pelajar Pancasila', Array.isArray(data.profil_pelajar_pancasila) ? data.profil_pelajar_pancasila.join(', ') : data.profil_pelajar_pancasila],
            ['Domain / Konten',         data.domain_konten],
            ['Model',                   modelPP.model],
            ['Pendekatan',              modelPP.pendekatan],
            ['Metode',                  Array.isArray(modelPP.metode) ? modelPP.metode.join(', ') : modelPP.metode],
            ['Target PD',               Array.isArray(data.target_peserta_didik) ? data.target_peserta_didik.join(', ') : data.target_peserta_didik],
            ['Sarana & Prasarana',      Array.isArray(data.sarana_prasarana) ? data.sarana_prasarana.join(', ') : data.sarana_prasarana],
          ]} />
        </Section>
        <Section title="Capaian & Tujuan Pembelajaran">
          <div className="mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Capaian Pembelajaran</p>
            <p className="text-sm text-slate-700">{data.fase_capaian_pembelajaran}</p>
          </div>
          <div className="mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Tujuan Pembelajaran</p>
            <BulletList items={data.tujuan_pembelajaran || []} />
          </div>
          <div className="mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Pemahaman Bermakna</p>
            <p className="text-sm text-slate-700">{data.pemahaman_bermakna}</p>
          </div>
          {(data.pertanyaan_pemantik || []).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Pertanyaan Pemantik</p>
              <BulletList items={data.pertanyaan_pemantik} />
            </div>
          )}
        </Section>
        <Section title="Langkah-Langkah Pembelajaran">
          <KegiatanTable kegiatan={langkah} />
        </Section>
        <Section title="Asesmen">
          {asesmen.diagnostik && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Diagnostik</p>
              <p className="text-sm text-slate-700">{asesmen.diagnostik}</p>
            </div>
          )}
          {formatif.instrumen && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-1">Formatif</p>
              <p className="text-sm text-slate-700">{formatif.instrumen}</p>
            </div>
          )}
          {(formatif.rubrik || []).length > 0 && (
            <div className="overflow-x-auto">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Rubrik Penilaian</p>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr style={{ background: 'linear-gradient(135deg,#065F46,#059669)' }}>
                    {['Aspek', 'Skor 4', 'Skor 3', 'Skor 2', 'Skor 1'].map(h => (
                      <th key={h} className="text-left px-3 py-2 text-white font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {formatif.rubrik.map((rb, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-green-50'}>
                      <td className="px-3 py-2 font-semibold">{rb.aspek}</td>
                      <td className="px-3 py-2">{rb.skor_4}</td>
                      <td className="px-3 py-2">{rb.skor_3}</td>
                      <td className="px-3 py-2">{rb.skor_2}</td>
                      <td className="px-3 py-2">{rb.skor_1}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
        {(data.remedial || data.pengayaan) && (
          <Section title="Remedial & Pengayaan">
            {data.remedial && <div className="mb-2"><span className="text-xs font-bold text-orange-600">REMEDIAL: </span><span className="text-sm text-slate-700">{data.remedial}</span></div>}
            {data.pengayaan && <div><span className="text-xs font-bold text-green-600">PENGAYAAN: </span><span className="text-sm text-slate-700">{data.pengayaan}</span></div>}
          </Section>
        )}
        {((data.refleksi_guru || []).length > 0 || (data.refleksi_siswa || []).length > 0) && (
          <Section title="Refleksi">
            <div className="grid grid-cols-2 gap-4">
              <div><p className="text-xs font-semibold text-slate-500 mb-1">Refleksi Guru</p><BulletList items={data.refleksi_guru || []} /></div>
              <div><p className="text-xs font-semibold text-slate-500 mb-1">Refleksi Siswa</p><BulletList items={data.refleksi_siswa || []} /></div>
            </div>
          </Section>
        )}
        {(data.glosarium || []).length > 0 && (
          <Section title="Glosarium">
            <div className="space-y-2">
              {data.glosarium.map((g, i) => (
                <div key={i} className="flex gap-2 text-sm">
                  <span className="font-semibold text-blue-700 min-w-[120px]">{g.istilah}</span>
                  <span className="text-slate-600">: {g.definisi}</span>
                </div>
              ))}
            </div>
          </Section>
        )}
        {data.lkpd && (
          <Section title={`LKPD — ${data.lkpd.judul || ''}`}>
            <p className="text-sm text-slate-700 mb-3 italic">{data.lkpd.petunjuk}</p>
            {(data.lkpd.aktivitas || []).map((ak, i) => (
              <div key={i} className="mb-4 border-l-4 border-yellow-400 pl-3">
                <p className="font-semibold text-slate-800 text-sm mb-1">Aktivitas {ak.nomor} — {ak.judul}</p>
                <p className="text-sm text-slate-700">{ak.instruksi}</p>
              </div>
            ))}
          </Section>
        )}
      </div>
    );
  }

  // Legacy format (old documents)
  const info = data.informasi_umum || {};
  const inti = data.komponen_inti || {};
  return (
    <div>
      <Section title="Informasi Umum">
        <InfoGrid items={[
          ['Mata Pelajaran', info.mata_pelajaran],
          ['Tema / Bab', info.tema_bab],
          ['Fase / Kelas', info.fase_kelas],
          ['Semester', info.semester],
          ['Tahun Pelajaran', info.tahun_pelajaran],
          ['Alokasi Waktu', info.alokasi_waktu],
          ['Model Pembelajaran', info.model_pembelajaran],
          ['Target PD', info.target_peserta_didik],
          ['P. Pancasila', info.profil_pelajar_pancasila?.join(', ')],
          ['Sarana', info.sarana_prasarana?.join(', ')],
        ]} />
      </Section>
      {inti.tujuan_pembelajaran?.length > 0 && (
        <Section title="Tujuan Pembelajaran">
          <BulletList items={inti.tujuan_pembelajaran} />
        </Section>
      )}
      {inti.kegiatan_pembelajaran?.length > 0 && (
        <Section title="Kegiatan Pembelajaran">
          {inti.kegiatan_pembelajaran.map((p, i) => (
            <div key={i} className="mb-4">
              <div className="font-semibold text-slate-800 text-sm mb-2">Pertemuan {p.pertemuan} — {p.topik}</div>
              <KegiatanTable kegiatan={{ pendahuluan: p.kegiatan_awal, inti: p.kegiatan_inti, penutup: p.kegiatan_penutup }} />
            </div>
          ))}
        </Section>
      )}
    </div>
  );
};

const KktpView = ({ data }) => (
  <div>
    <Section title="Identitas">
      <InfoGrid items={[
        ['Mata Pelajaran', data.header?.mata_pelajaran],
        ['Satuan Pendidikan', data.header?.satuan_pendidikan],
        ['Tahun Pelajaran', data.header?.tahun_pelajaran],
        ['Fase/Kelas/Semester', data.header?.fase_kelas_semester],
      ]} />
    </Section>
    {(data.bab_list || []).map((bab, i) => (
      <Section key={i} title={`${bab.bab}: ${bab.tema}`}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr>
                <th className="border border-slate-200 px-2 py-2 bg-purple-100 text-purple-800 w-8">No</th>
                <th className="border border-slate-200 px-2 py-2 bg-purple-100 text-purple-800">Alur Tujuan Pembelajaran</th>
                <th className="border border-slate-200 px-2 py-2 bg-red-50 text-red-700">0–40%<br/><span className="font-normal">Remedial Seluruh</span></th>
                <th className="border border-slate-200 px-2 py-2 bg-orange-50 text-orange-700">41–65%<br/><span className="font-normal">Remedial Sebagian</span></th>
                <th className="border border-slate-200 px-2 py-2 bg-green-50 text-green-700">66–85%<br/><span className="font-normal">Tuntas</span></th>
                <th className="border border-slate-200 px-2 py-2 bg-blue-50 text-blue-700">86–100%<br/><span className="font-normal">Pengayaan</span></th>
              </tr>
            </thead>
            <tbody>
              {(bab.alur_tujuan_pembelajaran || []).map((atp, j) => (
                <tr key={j} className={j % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="border border-slate-200 px-2 py-2 text-center font-semibold text-slate-600">{atp.no}</td>
                  <td className="border border-slate-200 px-2 py-2 text-slate-700">{atp.atp}</td>
                  <td className="border border-slate-200 px-2 py-2 text-slate-600">{atp.skala_0_40}</td>
                  <td className="border border-slate-200 px-2 py-2 text-slate-600">{atp.skala_41_65}</td>
                  <td className="border border-slate-200 px-2 py-2 text-slate-600">{atp.skala_66_85}</td>
                  <td className="border border-slate-200 px-2 py-2 text-slate-600">{atp.skala_86_100}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    ))}
  </div>
);

const PromesView = ({ data }) => (
  <div>
    <Section title="Identitas">
      <InfoGrid items={[
        ['Mata Pelajaran', data.header?.mata_pelajaran],
        ['Fase / Kelas', data.header?.fase_kelas],
        ['Semester', data.header?.semester],
        ['Tahun Pelajaran', data.header?.tahun_pelajaran],
      ]} />
    </Section>
    <Section title="Distribusi Materi">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr style={{ background: 'linear-gradient(135deg,#1E3A8A,#3B82F6)' }}>
              {['No','Bab / Tema','Alur Tujuan Pembelajaran','Alokasi JP','Bulan / Minggu'].map(h => (
                <th key={h} className="text-left px-3 py-2 text-white text-xs font-semibold border-r border-blue-700 last:border-0">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.distribusi_materi || []).map((item, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                <td className="border border-slate-200 px-3 py-2 font-semibold text-slate-600 text-center">{item.no}</td>
                <td className="border border-slate-200 px-3 py-2"><span className="font-semibold">{item.bab}</span><br/><span className="text-slate-500 text-xs">{item.tema}</span></td>
                <td className="border border-slate-200 px-3 py-2">
                  <ul>{(item.alur_tujuan_pembelajaran||[]).map((a,j)=><li key={j} className="text-xs text-slate-600">• {a}</li>)}</ul>
                </td>
                <td className="border border-slate-200 px-3 py-2 text-center font-semibold text-blue-700">{item.alokasi_jp} JP</td>
                <td className="border border-slate-200 px-3 py-2 text-xs">
                  {(item.bulan_minggu||[]).map((b,j)=><div key={j}>{b.bulan}: Minggu {(b.minggu||[]).join(', ')}</div>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-4 mt-3 text-sm text-slate-600">
        <span>Total JP: <strong>{data.total_jp}</strong></span>
        <span>JP Efektif: <strong>{data.jp_efektif}</strong></span>
        <span>JP Cadangan: <strong>{data.jp_cadangan}</strong></span>
      </div>
    </Section>
  </div>
);

const ProtaView = ({ data }) => (
  <div>
    <Section title="Identitas">
      <InfoGrid items={[
        ['Mata Pelajaran', data.header?.mata_pelajaran],
        ['Fase / Kelas', data.header?.fase_kelas],
        ['Tahun Pelajaran', data.header?.tahun_pelajaran],
      ]} />
    </Section>
    {['semester_ganjil','semester_genap'].map(semKey => {
      const sem = data[semKey];
      if (!sem) return null;
      return (
        <Section key={semKey} title={sem.nama}>
          <p className="text-xs text-slate-500 mb-2">Bulan: {(sem.bulan||[]).join(', ')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr style={{ background: 'linear-gradient(135deg,#1E3A8A,#3B82F6)' }}>
                  {['No','Bab / Tema','Capaian Pembelajaran','Alokasi JP'].map(h=>(
                    <th key={h} className="text-left px-3 py-2 text-white text-xs font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(sem.bab_list||[]).map((bab,i)=>(
                  <tr key={i} className={i%2===0?'bg-white':'bg-slate-50'}>
                    <td className="border border-slate-200 px-3 py-2 text-center font-semibold text-slate-600">{bab.no}</td>
                    <td className="border border-slate-200 px-3 py-2"><span className="font-semibold">{bab.bab}</span><br/><span className="text-xs text-slate-500">{bab.tema}</span></td>
                    <td className="border border-slate-200 px-3 py-2 text-slate-600 text-xs">{bab.capaian_pembelajaran}</td>
                    <td className="border border-slate-200 px-3 py-2 text-center font-semibold text-blue-700">{bab.alokasi_jp} JP</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm mt-2 text-slate-600">Total JP: <strong>{sem.total_jp}</strong> &nbsp;|&nbsp; Efektif: <strong>{sem.jp_efektif}</strong></p>
        </Section>
      );
    })}
    <p className="text-sm font-semibold text-slate-700">Total JP Setahun: <span className="text-blue-700">{data.total_jp_setahun}</span></p>
  </div>
);

const JadwalView = ({ data }) => {
  const jadwal = data.jadwal_mingguan || {};
  const hariList = ['Senin','Selasa','Rabu','Kamis','Jumat'];
  const maxJam = Math.max(...hariList.map(h => (jadwal[h]||[]).length), 0);
  return (
    <div>
      <Section title="Identitas">
        <InfoGrid items={[
          ['Kelas', `${data.header?.kelas} - ${data.header?.jenjang}`],
          ['Semester', data.header?.semester],
          ['Tahun Pelajaran', data.header?.tahun_pelajaran],
          ['Wali Kelas', data.header?.wali_kelas || '_______________'],
        ]} />
      </Section>
      <Section title="Jadwal Mingguan">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr style={{ background: 'linear-gradient(135deg,#1E3A8A,#3B82F6)' }}>
                <th className="px-3 py-2 text-white text-xs font-semibold text-left">Jam</th>
                <th className="px-3 py-2 text-white text-xs font-semibold text-left">Waktu</th>
                {hariList.map(h=><th key={h} className="px-3 py-2 text-white text-xs font-semibold text-left">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.from({length: maxJam}, (_,i) => {
                const jamInfo = (data.jam_pelajaran||[]).find(j=>j.jam_ke===i+1)||{};
                return (
                  <tr key={i} className={i%2===0?'bg-white':'bg-slate-50'}>
                    <td className="border border-slate-200 px-3 py-2 text-center font-bold text-blue-700">{i+1}</td>
                    <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{jamInfo.mulai}–{jamInfo.selesai}</td>
                    {hariList.map(h=>{
                      const entry = (jadwal[h]||[]).find(j=>j.jam_ke===i+1)||{};
                      return <td key={h} className="border border-slate-200 px-3 py-2 text-xs">{entry.mata_pelajaran||''}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
};

const RawView = ({ data }) => (
  <pre className="text-xs text-slate-600 bg-slate-50 rounded-xl p-4 overflow-auto max-h-[60vh]">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const RENDERERS = {
  'RPP':        RppView,
  'Modul Ajar': ModulAjarView,
  'KKTP':       KktpView,
  'Promes':     PromesView,
  'Prota':      ProtaView,
  'Jadwal':     JadwalView,
};

// ── Main Component ───────────────────────────────────────────────────────────

export const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/documents/${id}`, { withCredentials: true })
      .then(r => { r.data._id = r.data._id || id; setDoc(r.data); })
      .catch(() => setError('Dokumen tidak ditemukan.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin" />
      </div>
    </DashboardLayout>
  );

  if (error || !doc) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-slate-500">{error || 'Dokumen tidak ditemukan.'}</p>
        <button onClick={() => navigate('/documents')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold"
          style={{ background: 'linear-gradient(135deg,#1E3A8A,#3B82F6)' }}>
          <ArrowLeft size={14} /> Kembali
        </button>
      </div>
    </DashboardLayout>
  );

  const typeStyle = DOC_TYPE_STYLE[doc.document_type] || DOC_TYPE_STYLE['Modul Ajar'];
  const statusStyle = STATUS_STYLE[doc.status] || STATUS_STYLE['draft'];
  const StatusIcon = statusStyle.icon;
  const structured = doc.content?.structured || {};
  const Renderer = RENDERERS[doc.document_type] || RawView;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>

        {/* Back */}
        <button onClick={() => navigate('/documents')}
          className="flex items-center gap-2 text-slate-500 hover:text-blue-700 text-sm font-medium mb-5 transition-colors">
          <ArrowLeft size={16} /> Kembali ke Dokumen Saya
        </button>

        {/* Hero card */}
        <div className="rounded-2xl p-6 mb-6 text-white"
          style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 50%, #3B82F6 100%)' }}>
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${typeStyle.bg}`}>
              {(() => { const Icon = typeStyle.icon; return <Icon size={26} className={typeStyle.text} />; })()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${typeStyle.bg} ${typeStyle.text}`}>
                  {doc.document_type}
                </span>
                <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg ${statusStyle.bg} ${statusStyle.text}`}>
                  <StatusIcon size={11} />{statusStyle.label}
                </span>
              </div>
              <h1 className="text-xl font-bold leading-snug mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {doc.document_type} — {doc.subject_name}
              </h1>
              <div className="flex flex-wrap gap-4 text-blue-200 text-sm">
                <span className="flex items-center gap-1.5"><School size={13} /> Kelas {doc.kelas} {doc.jenjang}</span>
                <span className="flex items-center gap-1.5"><BookMarked size={13} /> Semester {doc.semester}</span>
                <span className="flex items-center gap-1.5"><Calendar size={13} /> {doc.tahun_ajaran}</span>
              </div>
            </div>
            <a
              href={`${API}/documents/${id}/download`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm flex-shrink-0 hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg,#059669,#10B981)', color: 'white' }}>
              <Download size={15} />
              Download .docx
            </a>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white border border-slate-100 rounded-2xl shadow-sm p-6">
          {(structured.parse_error || structured._truncated) ? (
            <ParseErrorBanner docId={id} onNavigate={() => navigate('/documents/generate')} />
          ) : Object.keys(structured).length > 0
            ? <Renderer data={structured} />
            : <RawView data={doc.content} />
          }
        </div>

      </div>
    </DashboardLayout>
  );
};
